<script>
  import { onMount, setContext } from 'svelte'

  let element, timer, children, transitioning, cssTransition

  export let delay = 10000
  export let count = 3

  export let transition = 1000
  const key = 'carousel-autorotate-method'

  $: cssTransition = `all ${transition / 1000}s`

  const getElementHeight = element => {
    let height = element.offsetHeight
    height += parseInt(
      window.getComputedStyle(element).getPropertyValue('margin-top')
    )
    height += parseInt(
      window.getComputedStyle(element).getPropertyValue('margin-bottom')
    )
    return height
  }

  const makeSlidesActive = element => {
    children = element.children
    if (
      window.matchMedia('(min-width: 1381px)').matches &&
      children.length >= 3
    ) {
      count = 3
    }

    if (
      (window.matchMedia('(max-width: 1380px)').matches &&
        children.length >= 2) ||
      (window.matchMedia('(min-width: 1381px)').matches &&
        children.length === 2)
    ) {
      count = 2
    }
    if (
      window.matchMedia('(max-width: 969px)').matches ||
      children.length === 1
    ) {
      count = 1
    }

    for (let i = 0; i < children.length; i++) {
      if (i < count) {
        children[i].classList.add('active')
        children[i].setAttribute('aria-hidden', false)
      } else {
        children[i].classList.remove('active')
        children[i].setAttribute('aria-hidden', true)
      }
    }
    return children
  }

  $: if (element) {
    makeSlidesActive(element)
  }

  onMount(() => {
    if (element) {
      const children = makeSlidesActive(element)
      const childHeight = getElementHeight(children[0])

      element.style.height = `${childHeight}px`

      for (let i = 0; i < children.length; i++) {
        element.children[i].style.transition = cssTransition
      }
      start()
    }
  })

  export const next = () => {
    if (transitioning) {
      return
    }
    transitioning = true
    const children = element.children
    let foundActive = false
    let previousElem
    let nextElem
    // Find the first one marked active, then the first one not active.
    for (let i = 0; i < children.length; i++) {
      if (!foundActive && children[i].classList.contains('active')) {
        previousElem = children[i]
        foundActive = true
        continue
      }
      if (foundActive && !children[i].classList.contains('active')) {
        nextElem = children[i]
        break
      }
    }

    // Wait the transition time, then move the previous element to the end.
    if (nextElem) {
      setTimeout(() => {
        nextElem.classList.add('active')
        nextElem.setAttribute('aria-hidden', false)
        previousElem.classList.remove('active')
        previousElem.setAttribute('aria-hidden', true)
        element.style.transition = ''
        element.appendChild(previousElem)
        transitioning = false
      }, transition)
    }
    // Restart the timer.
    if (timer) {
      start()
    }
  }

  export const prev = () => {
    if (transitioning) {
      return
    }
    transitioning = true
    const children = element.children
    let foundLastItem = false
    const activeElements = []

    // Find the first one marked active, then the first one not active.
    for (let i = 0; i < children.length; i++) {
      if (!foundLastItem && children[i].classList.contains('active')) {
        activeElements.push(children[i])
      }
    }

    const lastItem = activeElements[activeElements.length - 1]
    if (lastItem) {
      foundLastItem = true
    }
    const endItem = children[children.length - 1]

    element.style.transition = cssTransition

    // Wait the transition time, then move the previous element to the end.
    setTimeout(() => {
      lastItem.classList.remove('active')
      lastItem.setAttribute('aria-hidden', true)
      endItem.classList.add('active')
      endItem.setAttribute('aria-hidden', false)
      element.prepend(endItem)
      transitioning = false
    }, transition)

    if (timer) {
      start()
    }
  }

  export const pause = () => {
    clearInterval(timer)
    timer = null
  }

  export const start = () => {
    if (delay > 0) {
      if (timer) pause()
      timer = setInterval(next, delay)
    }
  }

  setContext(key, { start })
</script>

<style>
  #carousel {
    text-align: center;
    overflow: hidden;
    position: relative;
    margin-right: 35px;
    margin-left: 35px;
  }

  @media (max-width: 519px) {
    #carousel {
      margin-right: 0px;
      margin-left: 0px;
    }
  }

  .previous,
  .next {
    cursor: pointer;
    z-index: 1;
    fill: #c5b4bf;
    border: none;
    background: transparent;
  }

  .slider {
    display: flex;
    text-align: center;
    position: relative;
    justify-content: center;
    flex-wrap: wrap;
  }

  .slider > :global(*.active) {
    min-height: 9px;
    vertical-align: middle;
    display: flex;
  }

  .slider > :global(*) {
    display: none;
  }

  #carousel > :global(*.notransition) {
    -webkit-transition: none !important;
    -moz-transition: none !important;
    -o-transition: none !important;
    transition: none !important;
  }

  .carousel__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>

<div
  class="$style.carousel__wrapper"
  aria-label="reviews"
  aria-roledescription="carousel">
  <button
    class="$style.previous"
    aria-label="click to change to previous slide"
    on:click={prev}>
    <svg
      width="28"
      height="28"
      viewBox="0 0 1792 1792"
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="0" y="0" width="200%" height="200%">
          <feOffset result="offOut" in="SourceAlpha" dx="0" dy="0" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="10" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
      </defs>
      <path
        filter="url(#shadow)"
        d="M1427 301l-531 531 531 531q19 19 19 45t-19 45l-166 166q-19 19-45
        19t-45-19l-742-742q-19-19-19-45t19-45l742-742q19-19 45-19t45 19l166
        166q19 19 19 45t-19 45z" />
    </svg>
  </button>

  <div id="carousel">
    <div
      bind:this={element}
      class="$style.slider"
      on:mouseover={pause}
      on:mouseout={start}>
      <slot />
    </div>
  </div>

  <button
    aria-label="click to change to nextss slide"
    class="$style.next"
    on:click={next}>
    <svg
      width="28"
      height="28"
      viewBox="0 0 1792 1792"
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="0" y="0" width="200%" height="200%">
          <feOffset result="offOut" in="SourceAlpha" dx="0" dy="0" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="10" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
      </defs>
      <path
        filter="url(#shadow)"
        d="M1363 877l-742 742q-19 19-45
        19t-45-19l-166-166q-19-19-19-45t19-45l531-531-531-531q-19-19-19-45t19-45l166-166q19-19
        45-19t45 19l742 742q19 19 19 45t-19 45z" />
    </svg>
  </button>
</div>
